/****************************************************
 * Module Name:  CompanyMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Tooltip, Button } from "@mui/material";
import NoPermission from "../../common/noPermission";
import React, { useState, useEffect } from "react";
import Constants from "../../../utility/constants";
import CommonSnackbar from "../../common/commonSnackBar";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import Utility from "../../../utility/utility";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import renderCellExpand from "../../common/dataGridCellExpand";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import TenderCompanyProxy from "../../../utility/proxy/manager/manufacturingProxy/tenderCompanyProxy";
import AddCompanyModal from "./addCompanyModal";
import PageTitle from "../../common/pageTitle";

const CompanyMaster = () => {
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [companyListLoader, setCompanyListLoader] = useState(false);
    const [companyList, setCompanyList] = useState([]);
    const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
    const [editOperation, setEditOperation] = useState(false);
    const [sourceData, setSourceData] = useState({});
    const [loadingButton, setLoadingButton] = useState(false);
    const [SuccessSnackbar, setSuccessSnackbar] = useState(false);
    const [companyOperation, setCompanyOperation] = useState('create');
    const [filterValues, setFilterValues] = useState([]);

    const local_Permission = Constants.USER_PERMISSION.filter(
        (iPermission) => iPermission.permissionid === Constants.PERMISSIONS.TENDER_MASTER_COMPANY);

    useEffect(() => {
        PageTitle("TENDER_MASTER_COMPANY");
        checkPermission();
    }, []);

    const checkPermission = () => {
        if (Utility.isReadAllowed(local_Permission)) {
            fetchCompanyData(true);
        }
    }

    const fetchCompanyData = (bAnimateSpinner) => {
        setCompanyListLoader(true);
        bAnimateSpinner && setCompanyListLoader(true);
        TenderCompanyProxy.GetAllCompanies(
            (responseData) => {
                if (responseData) {
                    const uniqueFilterValues = {
                        companyname: Utility.getDistinctOptions(responseData, "name"),
                        phonecode: Utility.getDistinctOptions(responseData, "phonecode"),
                    };
                    setFilterValues(uniqueFilterValues);
                    setCompanyList(responseData);
                    setCompanyListLoader(false);
                } else {
                    setErrorSnackbar(true);
                    setCompanyListLoader(false);
                }
            }
        )
    };

    const initialColumns = () => [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "name",
            headerName: "COMPANY NAME",
            headerAlign: "center",
            align: "center",
            width: 250,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.companyname : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "logo",
            headerName: "LOGO",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: (params) => (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    {params.value ? (
                        <img
                            src={`data:image/jpeg;base64,${params.value}`}
                            alt="Company Logo"
                            style={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                marginRight: 10,
                                objectFit: "cover",
                            }}
                        />
                    ) : null
                    }
                </div>
            ),
        },
        {
            field: "phonecode",
            headerName: "PHONE CODE",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.phonecode : [],
            filterOperators: Utility.combinedFilters(),
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "contactno",
            headerName: "CONTACT NO",
            headerAlign: "center",
            align: "center",
            width: 250,
            renderCell: renderCellExpand,
        },
        {
            field: "email",
            headerName: "EMAIL",
            headerAlign: "center",
            align: "center",
            width: 250,
            renderCell: renderCellExpand,
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setEditOperation(true)
                            setShowAddCompanyModal(true)
                            setSourceData(params.row)
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        }

    ]

    const addOrUpdateCompanyData = (companyData, logo, changingPhoto) => {
        setLoadingButton(true);
        setCompanyOperation(editOperation ? 'update' : 'create');
        if (editOperation) {
            TenderCompanyProxy.UpdateCompany(companyData, (response) => {
                setLoadingButton(false);
                if (response) {
                    changingPhoto ? addOrRemoveCompanyLogo(logo, companyData.id) : fetchCompanyData(true);
                    setShowAddCompanyModal(false);
                    setCompanyListLoader(false);
                    setSuccessSnackbar(true);
                }
                else {
                    setErrorSnackbar(true);
                    setCompanyListLoader(false);
                }
            });
        } else {
            TenderCompanyProxy.AddCompany(companyData, (response) => {
                setLoadingButton(false);
                if (response) {
                    changingPhoto ? addOrRemoveCompanyLogo(logo, response.id) : fetchCompanyData(true);
                    setShowAddCompanyModal(false);
                    setCompanyListLoader(false);
                    setSuccessSnackbar(true);
                }
                else {
                    setErrorSnackbar(true);
                    setCompanyListLoader(false);
                }
            });
        }
    }


    const addOrRemoveCompanyLogo = (logo, id) => {
        let profileData = new FormData();
        profileData.append("file", logo);

        if (logo === null) {
            TenderCompanyProxy.RemoveCompanyLogo(
                id,
                profileData,
                (response) => {
                    if (response) {
                        fetchCompanyData(true);
                    } else {
                        setErrorSnackbar(true);
                        setCompanyListLoader(false);
                    }
                }
            );
        } else {
            TenderCompanyProxy.AddCompanyLogo(
                id,
                profileData,
                (response) => {
                    if (response) {
                        fetchCompanyData(true);
                    } else {
                        setErrorSnackbar(true);
                        setCompanyListLoader(false);
                    }
                }
            );
        }
    }


    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: "block" }} />
                {companyListLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            color="primary"
                            fontSize="medium"
                            onClick={() => fetchCompanyData(true)}
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                {Utility.isAddAllowed(local_Permission) ? (
                    <Tooltip title="Add Company">
                        <Button
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ ml: "5px" }}
                            onClick={() => {
                                setShowAddCompanyModal(true);
                                setEditOperation(false);
                                setSourceData({});
                            }
                            }
                            startIcon={<AddBoxOutlinedIcon size="large" />}
                        >
                            Company
                        </Button>
                    </Tooltip>
                ) : null}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }
    const handleClose = () => {
        setSuccessSnackbar(false);
    };

    const handleValidation = (companyData) => {
        //TODO do validation if needed here
        return true;
    };

    const columns = Utility.isUpdateAllowed(local_Permission)
        ? initialColumns()
        : initialColumns().filter((col) => col.field !== "edit");

    return !Utility.isReadAllowed(local_Permission) ? <NoPermission /> : (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {SuccessSnackbar ? (
                <CommonSnackbar
                    show={SuccessSnackbar}
                    message={`Company ${companyOperation === 'update' ? 'updated' : 'created'} successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}
            {showAddCompanyModal ? (
                <AddCompanyModal
                    Show={true}
                    handleClose={() => setShowAddCompanyModal(false)}
                    handleSave={(data, logo, changingPhoto) => addOrUpdateCompanyData(data, logo, changingPhoto)}
                    handleValidation={() => handleValidation()}
                    editOperation={editOperation}
                    sourceData={sourceData}
                    loading={loadingButton}
                />
            ) : null}
            {
                (
                    <Box px={1}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "83vh",
                                "& .lavenderCell": {
                                    color: "#080808",
                                    backgroundColor: "#D8BFD8",
                                },
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                density="compact"
                                loading={companyListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: CustomToolbar }}
                                rows={companyList}
                                columns={columns}
                                getRowId={(row) => row.id}
                                rowSpacingType="border"
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )
            }
        </Box>
    );
}

export default CompanyMaster;